import apiClient from '@lib/apiClient';
import { CountryList, FilesUploadResponse } from '@services/types';

import { AdminPanelApiUrl } from './endpoints';
import {
    AgentConfig,
    AgentsConfig,
    AvatarSupportedLanguages,
    FilesDataToUpdate,
    FileUploadConstraints,
    GeneralKnowledgeFiles,
    GroupsWithPromptsRequest,
    RolesWithPromptsRequest,
} from './types';

export const getAllAgents = async (): Promise<AgentsConfig> => {
    const response = await apiClient.get<AgentsConfig>(AdminPanelApiUrl.AllAgents);
    return response.data;
};

export const updateAgent = async (agent: AgentConfig): Promise<void> => {
    const response = await apiClient.put<void>(AdminPanelApiUrl.EditAgent, agent);
    return response.data;
};

export const getGeneralKnowledgeFiles = async (): Promise<GeneralKnowledgeFiles> => {
    const response = await apiClient.get<GeneralKnowledgeFiles>(
        AdminPanelApiUrl.GeneralKnowledgeFiles
    );
    return response.data;
};

export const updateGeneralKnowledgeFiles = async (
    data: FilesDataToUpdate
): Promise<FilesUploadResponse> => {
    const formData = new FormData();

    data.forEach((item, index) => {
        formData.append(`files[${index}].File`, item.file);
        formData.append(`files[${index}].Country`, item.country);
    });

    const response = await apiClient.put<FilesUploadResponse>(
        AdminPanelApiUrl.UpdateGeneralKnowledgeFiles,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return response.data;
};

export const deleteGeneralKnowledgeFile = async (absolutePath: string): Promise<void> => {
    const response = await apiClient.delete<void>(AdminPanelApiUrl.DeleteGeneralKnowledgeFile, {
        params: {
            absolutePath,
        },
    });
    return response.data;
};

export const getFileUploadConstraints = async (): Promise<FileUploadConstraints> => {
    const response = await apiClient.get<FileUploadConstraints>(
        AdminPanelApiUrl.FileUploadConstraints
    );
    return response.data;
};

export const getAvatarSupportedLanguages = async (): Promise<AvatarSupportedLanguages> => {
    const response = await apiClient.get<AvatarSupportedLanguages>(
        AdminPanelApiUrl.AvatarSupportedLanguages
    );
    return response.data;
};

export const updateAvatarSupportedLanguages = async (
    languages: AvatarSupportedLanguages
): Promise<void> => {
    const response = await apiClient.put<void>(
        AdminPanelApiUrl.AvatarSupportedLanguages,
        languages
    );
    return response.data;
};

export const updatePredefinedPromptsByRole = async (
    roles: RolesWithPromptsRequest
): Promise<void> => {
    const response = await apiClient.put<void>(
        AdminPanelApiUrl.UpdatePredefinedPromptsByRole,
        roles
    );
    return response.data;
};

export const deletePredefinedPromptByRole = async (
    roleId: string,
    promptId: string
): Promise<void> => {
    const response = await apiClient.delete<void>(
        AdminPanelApiUrl.deletePredefinedPromptByRole(roleId, promptId)
    );
    return response.data;
};

export const updateGeneralPredefinedPrompts = async (
    generalPredefinedPrompts: GroupsWithPromptsRequest
): Promise<void> => {
    const response = await apiClient.put<void>(
        AdminPanelApiUrl.UpdateGeneralPredefinedPrompts,
        generalPredefinedPrompts
    );
    return response.data;
};

export const deleteGeneralPredefinedPrompt = async (
    groupId: string,
    promptId: string
): Promise<void> => {
    const response = await apiClient.delete<void>(
        AdminPanelApiUrl.deleteGeneralPredefinedPrompt(groupId, promptId)
    );
    return response.data;
};

export const deleteGeneralPredefinedPromptGroup = async (groupId: string): Promise<void> => {
    const response = await apiClient.delete<void>(
        AdminPanelApiUrl.deleteGeneralPredefinedPromptGroup(groupId)
    );
    return response.data;
};

export const getSupportedCountriesForAdminPanel = async (): Promise<CountryList> => {
    const response = await apiClient.get<CountryList>(AdminPanelApiUrl.SupportedCountries);
    return response.data;
};
